
import React from "react";
import {ucFirst} from "@/utils/textFormatting";
import {PaginatedExtractionJob, PersistedExtractionJob} from "@/types";
import {Tooltip} from 'flowbite-react';
import {faCircleExclamation, faCircleXmark} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import Spinner from "@/Components/Spinner";
import {ExtractionMethod, ExtractionJobStatus} from "@/types/enums";
import {isJobRunning, isJobStarting} from "@/services/extractionJobManager";

export default function JobStatusIndicator({job}: { job: PaginatedExtractionJob }) {
    const status = job.status || ExtractionJobStatus.Submitted;
    const progress = job.progress === undefined ? null : job.progress;
    let missingSubtitles = false;
    let tooltipContent: React.ReactNode;

    if (isJobStarting(status) && progress === null) {
        tooltipContent = 'Starting';
    } else if (isJobRunning(status)) {
        tooltipContent = "Processing";
    } else if (
        status === ExtractionJobStatus.Succeeded &&
        job.method !== 'manual' &&
        (!job.subtitle_count || isSubtitlesLikelyEmpty(job))
    ) {
        tooltipContent = emptySubtitlesErrorMessage(job)
        missingSubtitles = true;
    } else {
        tooltipContent = ucFirst(status);
    }

    return (
        <Tooltip
            theme={{target: 'flex items-center w-fit'}}
            content={<span className="max-w-60 inline-block">{tooltipContent}</span>}
            placement="auto"
        >
        <span className="inline-flex items-center">
            {
                isJobRunning(status) ?
                    (
                        <span role="status" className="inline-flex items-center">
                            {
                                progress === null ?
                                    (
                                        <span className="inline-flex items-center">
                                <Spinner/>
                            <span className="sr-only">Loading...</span>
                            </span>
                                    ) :
                                    (<span
                                        className="text-blue-600 text-sm dark:text-blue-400 select-none notranslate">
                                        <span>{Math.round(progress)}</span><span>%</span>
                                    </span>)
                            }

                        </span>
                    ) :


                    [ExtractionJobStatus.Failed, ExtractionJobStatus.Cancelled].includes(status) ?
                        (status === ExtractionJobStatus.Cancelled ?
                                <FontAwesomeIcon className="text-red-700" size="lg" icon={faCircleXmark}/> :
                                <FontAwesomeIcon className="text-red-700" size="lg" icon={faCircleExclamation}/>
                        )
                        :
                        (missingSubtitles ?
                                <FontAwesomeIcon className="text-yellow-500" size="lg" icon={faCircleExclamation}/>
                                :
                                <svg className="w-5 h-5 text-green-500 dark:text-green-400 flex-shrink-0"
                                     aria-hidden="true"
                                     xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                        d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z"/>
                                </svg>
                        )
            }
        </span>
        </Tooltip>
    )
}

function emptySubtitlesErrorMessage(job: PaginatedExtractionJob): React.ReactNode {
    if(job.subtitle_position_detection_failure === 'ambiguous_position') {
        const faqRegionUrl = route('faqs') + '#how-can-i-limit-the-region-of-the-frame-where-subtitles-are-extracted-from';
        const customRegionLink = <a href={faqRegionUrl} target="_blank" className="underline font-bold">custom extraction region</a>;
        return job.subtitle_count
            ? <span>Subtitle position could not be detected. Subtitles may be incomplete. Try setting a {customRegionLink} to improve results.</span>
            : <span>Subtitle position could not be detected. No subtitles were extracted. Try setting a {customRegionLink} to improve results.</span>;
    } else if (!job.subtitle_count) {
        return job.method === ExtractionMethod.OCR ? "No subtitles found. Ensure the video has visible subtitles, or try using the audio method to the extract subtitles." :
            "No subtitles found. Ensure the audio contains speech.";
    } else {
        return job.method === ExtractionMethod.OCR ? "Subtitles may be incomplete. Ensure the video has visible subtitles, or try using the audio method to the extract subtitles." :
            "Subtitles may be incomplete. Ensure the audio contains speech."
    }
}

function isSubtitlesLikelyEmpty(job: PaginatedExtractionJob) {
    const durationMinutes = job.file_duration / 60;
    const minSubtitlePerMinute = job.subtitle_position_detection_failure? 2 : 1;
    return job.subtitle_count! < Math.ceil(durationMinutes * minSubtitlePerMinute)
}
